import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import {
  Users, Plus, Search, ArrowLeft, Loader2, Trash2, Pencil,
  Phone, Mail, Globe, Tag, MapPin, Calendar, Bell, CheckCircle,
  Briefcase, Clock, X, StickyNote,
} from 'lucide-react';

// --- Add/Edit Client Dialog ---
const ClientFormDialog = ({ open, onClose, client, onSaved }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', nationality: '', tags: '', notes: '', preferences: '' });
  const [saving, setSaving] = useState(false);
  const isEdit = !!client;

  useEffect(() => {
    if (client) {
      setForm({ name: client.name || '', email: client.email || '', phone: client.phone || '', nationality: client.nationality || '', tags: (client.tags || []).join(', '), notes: client.notes || '', preferences: client.preferences || '' });
    } else {
      setForm({ name: '', email: '', phone: '', nationality: '', tags: '', notes: '', preferences: '' });
    }
  }, [client, open]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Client name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean) };
      if (isEdit) {
        await api.put(`/clients/${client.id}`, payload);
        toast.success('Client updated');
      } else {
        await api.post('/clients', payload);
        toast.success('Client added');
      }
      onSaved();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save client');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif">{isEdit ? 'Edit Client' : 'Add New Client'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Full name" data-testid="client-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Email</Label><Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="email@example.com" data-testid="client-email-input" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+61..." data-testid="client-phone-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Nationality</Label><Input value={form.nationality} onChange={e => setForm({...form, nationality: e.target.value})} placeholder="e.g., Australian" /></div>
            <div><Label>Tags</Label><Input value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} placeholder="VIP, Honeymoon, Corporate" data-testid="client-tags-input" /></div>
          </div>
          <div><Label>Travel Preferences</Label><Input value={form.preferences} onChange={e => setForm({...form, preferences: e.target.value})} placeholder="e.g., Business class, 5-star hotels" /></div>
          <div><Label>Notes</Label><textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Internal notes about this client..." className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-ring" data-testid="client-notes-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-gold hover:bg-gold-light text-ocean rounded-full font-semibold" data-testid="client-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : isEdit ? 'Update' : 'Add Client'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// --- Add Trip Dialog ---
const TripFormDialog = ({ open, onClose, clientId, onSaved }) => {
  const [form, setForm] = useState({ destination: '', start_date: '', end_date: '', budget: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.destination.trim()) { toast.error('Destination is required'); return; }
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/trips`, form);
      toast.success('Trip added');
      onSaved();
      onClose();
      setForm({ destination: '', start_date: '', end_date: '', budget: '', notes: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add trip');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-serif">Add Client Trip</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Destination *</Label><Input value={form.destination} onChange={e => setForm({...form, destination: e.target.value})} placeholder="e.g., Bali, Indonesia" data-testid="trip-destination-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} /></div>
            <div><Label>End Date</Label><Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} /></div>
          </div>
          <div><Label>Budget</Label><Input value={form.budget} onChange={e => setForm({...form, budget: e.target.value})} placeholder="e.g., A$5,000" /></div>
          <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Trip notes..." /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-gold hover:bg-gold-light text-ocean rounded-full font-semibold" data-testid="trip-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Trip'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// --- Add Reminder Dialog ---
const ReminderFormDialog = ({ open, onClose, clientId, onSaved }) => {
  const [form, setForm] = useState({ title: '', due_date: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/reminders`, form);
      toast.success('Reminder added');
      onSaved();
      onClose();
      setForm({ title: '', due_date: '', notes: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add reminder');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-serif">Add Reminder</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="e.g., Follow up on Bali trip" data-testid="reminder-title-input" /></div>
          <div><Label>Due Date</Label><Input type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} /></div>
          <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Additional notes..." /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-gold hover:bg-gold-light text-ocean rounded-full font-semibold" data-testid="reminder-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Reminder'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// --- Main Page ---
export default function AgentClientsPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [allTags, setAllTags] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [showClientForm, setShowClientForm] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showTripForm, setShowTripForm] = useState(false);
  const [showReminderForm, setShowReminderForm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchClients = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filterTag) params.set('tag', filterTag);
      const res = await api.get(`/clients?${params.toString()}`);
      if (res.data.success) {
        setClients(res.data.clients);
        setAllTags(res.data.all_tags);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Agent or Business membership required to access client management');
      }
    } finally { setLoading(false); }
  }, [search, filterTag]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/clients/dashboard/stats');
      if (res.data.success) setStats(res.data);
    } catch { /* silent */ }
  }, []);

  const fetchClientDetail = useCallback(async (clientId) => {
    try {
      const res = await api.get(`/clients/${clientId}`);
      if (res.data.success) setClientDetail(res.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/auth'); return; }
    fetchClients();
    fetchStats();
  }, [isAuthenticated, navigate, fetchClients, fetchStats]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/clients/${deleteTarget.id}`);
      toast.success('Client deleted');
      setClients(clients.filter(c => c.id !== deleteTarget.id));
      if (selectedClient === deleteTarget.id) { setSelectedClient(null); setClientDetail(null); }
      fetchStats();
    } catch { toast.error('Failed to delete'); }
    finally { setDeleting(false); setDeleteTarget(null); }
  };

  const handleSelectClient = (clientId) => {
    setSelectedClient(clientId);
    fetchClientDetail(clientId);
  };

  const handleToggleReminder = async (clientId, reminderId, completed) => {
    try {
      await api.put(`/clients/${clientId}/reminders/${reminderId}`, { completed: !completed });
      fetchClientDetail(clientId);
    } catch { /* silent */ }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="agent-clients-page">
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-ocean via-ocean-light/90 to-sky/70" />
        <div className="absolute top-32 right-20 w-96 h-96 bg-gold/15 rounded-full blur-3xl animate-pulse" />
      </div>
      <Header />

      <main className="pt-24 pb-16 px-6 md:px-12 lg:px-24 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <button onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white text-sm flex items-center gap-1 mb-2"><ArrowLeft className="w-4 h-4" /> Back</button>
              <h1 className="font-serif text-3xl text-white drop-shadow-lg">Client Management</h1>
            </div>
            <Button onClick={() => { setEditClient(null); setShowClientForm(true); }} className="bg-gold hover:bg-gold-light text-ocean rounded-full font-semibold px-5" data-testid="add-client-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Client
            </Button>
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-3 gap-4 mb-6">
              {[
                { label: 'Clients', value: stats.total_clients, icon: <Users className="w-5 h-5" /> },
                { label: 'Trips', value: stats.total_trips, icon: <MapPin className="w-5 h-5" /> },
                { label: 'Reminders', value: stats.pending_reminders, icon: <Bell className="w-5 h-5" /> },
              ].map(s => (
                <Card key={s.label} className="bg-white/95 backdrop-blur-xl border-white/30">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-ocean/10 text-ocean flex items-center justify-center">{s.icon}</div>
                    <div><p className="text-2xl font-bold text-ocean">{s.value}</p><p className="text-xs text-ocean/60">{s.label}</p></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <div className="grid lg:grid-cols-12 gap-6">
            {/* Client List */}
            <div className="lg:col-span-5">
              <Card className="bg-white/95 backdrop-blur-xl border-white/30 shadow-xl">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ocean/40" />
                      <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search clients..." className="pl-9 h-9 border-ocean/20" data-testid="client-search-input" />
                    </div>
                    {allTags.length > 0 && (
                      <select value={filterTag} onChange={e => setFilterTag(e.target.value)} className="h-9 px-2 border border-ocean/20 rounded-md text-sm text-ocean bg-white" data-testid="client-tag-filter">
                        <option value="">All tags</option>
                        {allTags.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="max-h-[500px] overflow-y-auto space-y-2 pt-0">
                  {loading ? (
                    <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-ocean" /></div>
                  ) : clients.length === 0 ? (
                    <div className="text-center py-8">
                      <Users className="w-10 h-10 text-ocean/20 mx-auto mb-2" />
                      <p className="text-ocean/50 text-sm">No clients yet. Add your first client!</p>
                    </div>
                  ) : clients.map(c => (
                    <div key={c.id} onClick={() => handleSelectClient(c.id)}
                      className={`p-3 rounded-xl cursor-pointer transition-all flex items-center gap-3 ${selectedClient === c.id ? 'bg-ocean/10 border border-ocean/30' : 'hover:bg-ocean/5 border border-transparent'}`}
                      data-testid={`client-row-${c.id}`}>
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-ocean to-sky flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                        {c.name?.charAt(0)?.toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-ocean text-sm truncate">{c.name}</p>
                        <p className="text-xs text-ocean/50 truncate">{c.email || c.phone || 'No contact info'}</p>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        {c.tags?.slice(0, 2).map(t => <span key={t} className="text-[10px] px-1.5 py-0.5 bg-gold/15 text-gold rounded-full">{t}</span>)}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            {/* Client Detail */}
            <div className="lg:col-span-7">
              {clientDetail ? (
                <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
                  <Card className="bg-white/95 backdrop-blur-xl border-white/30 shadow-xl">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="font-serif text-xl text-ocean">{clientDetail.client.name}</CardTitle>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => { setEditClient(clientDetail.client); setShowClientForm(true); }} className="text-ocean"><Pencil className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(clientDetail.client)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-5">
                      {/* Contact Info */}
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        {clientDetail.client.email && <div className="flex items-center gap-2 text-ocean/70"><Mail className="w-4 h-4" />{clientDetail.client.email}</div>}
                        {clientDetail.client.phone && <div className="flex items-center gap-2 text-ocean/70"><Phone className="w-4 h-4" />{clientDetail.client.phone}</div>}
                        {clientDetail.client.nationality && <div className="flex items-center gap-2 text-ocean/70"><Globe className="w-4 h-4" />{clientDetail.client.nationality}</div>}
                        {clientDetail.client.preferences && <div className="flex items-center gap-2 text-ocean/70"><Briefcase className="w-4 h-4" />{clientDetail.client.preferences}</div>}
                      </div>

                      {clientDetail.client.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {clientDetail.client.tags.map(t => <span key={t} className="text-xs px-2 py-1 bg-gold/15 text-gold rounded-full flex items-center gap-1"><Tag className="w-3 h-3" />{t}</span>)}
                        </div>
                      )}

                      {clientDetail.client.notes && (
                        <div className="bg-ocean/5 rounded-xl p-3"><p className="text-xs text-ocean/60 flex items-center gap-1 mb-1"><StickyNote className="w-3 h-3" /> Notes</p><p className="text-sm text-ocean">{clientDetail.client.notes}</p></div>
                      )}

                      {/* Trips */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-semibold text-ocean text-sm flex items-center gap-1"><MapPin className="w-4 h-4" /> Trips ({clientDetail.trips?.length || 0})</h3>
                          <Button variant="outline" size="sm" onClick={() => setShowTripForm(true)} className="text-xs rounded-full border-ocean/20 text-ocean" data-testid="add-trip-btn"><Plus className="w-3 h-3 mr-1" /> Add Trip</Button>
                        </div>
                        {clientDetail.trips?.length > 0 ? (
                          <div className="space-y-2">
                            {clientDetail.trips.map(trip => (
                              <div key={trip.id} className="p-3 bg-sky/5 rounded-lg border border-sky/10">
                                <div className="flex items-center justify-between">
                                  <p className="font-medium text-ocean text-sm">{trip.destination}</p>
                                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${trip.status === 'completed' ? 'bg-green-100 text-green-700' : trip.status === 'booked' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>{trip.status}</span>
                                </div>
                                {(trip.start_date || trip.end_date) && <p className="text-xs text-ocean/50 mt-1 flex items-center gap-1"><Calendar className="w-3 h-3" />{trip.start_date} {trip.end_date && `→ ${trip.end_date}`}</p>}
                                {trip.budget && <p className="text-xs text-ocean/50">Budget: {trip.budget}</p>}
                              </div>
                            ))}
                          </div>
                        ) : <p className="text-xs text-ocean/40">No trips recorded yet</p>}
                      </div>

                      {/* Reminders */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-semibold text-ocean text-sm flex items-center gap-1"><Bell className="w-4 h-4" /> Reminders</h3>
                          <Button variant="outline" size="sm" onClick={() => setShowReminderForm(true)} className="text-xs rounded-full border-ocean/20 text-ocean" data-testid="add-reminder-btn"><Plus className="w-3 h-3 mr-1" /> Add</Button>
                        </div>
                        {clientDetail.reminders?.length > 0 ? (
                          <div className="space-y-2">
                            {clientDetail.reminders.map(rem => (
                              <div key={rem.id} className={`p-3 rounded-lg border flex items-center gap-3 ${rem.completed ? 'bg-green-50/50 border-green-100' : 'bg-amber-50/50 border-amber-100'}`}>
                                <button onClick={() => handleToggleReminder(selectedClient, rem.id, rem.completed)}
                                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${rem.completed ? 'border-green-500 bg-green-500' : 'border-amber-400'}`}>
                                  {rem.completed && <CheckCircle className="w-3 h-3 text-white" />}
                                </button>
                                <div className="flex-1">
                                  <p className={`text-sm ${rem.completed ? 'text-green-700 line-through' : 'text-ocean'}`}>{rem.title}</p>
                                  {rem.due_date && <p className="text-xs text-ocean/50 flex items-center gap-1"><Clock className="w-3 h-3" />{rem.due_date}</p>}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : <p className="text-xs text-ocean/40">No reminders</p>}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ) : (
                <Card className="bg-white/95 backdrop-blur-xl border-white/30 shadow-xl">
                  <CardContent className="p-12 text-center">
                    <Users className="w-16 h-16 text-ocean/20 mx-auto mb-4" />
                    <h3 className="font-serif text-xl text-ocean/50 mb-2">Select a Client</h3>
                    <p className="text-ocean/40 text-sm">Choose a client from the list to view their details, trips, and reminders.</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Dialogs */}
      <ClientFormDialog open={showClientForm} onClose={() => setShowClientForm(false)} client={editClient} onSaved={() => { fetchClients(); fetchStats(); if (selectedClient) fetchClientDetail(selectedClient); }} />
      {selectedClient && <TripFormDialog open={showTripForm} onClose={() => setShowTripForm(false)} clientId={selectedClient} onSaved={() => fetchClientDetail(selectedClient)} />}
      {selectedClient && <ReminderFormDialog open={showReminderForm} onClose={() => setShowReminderForm(false)} clientId={selectedClient} onSaved={() => { fetchClientDetail(selectedClient); fetchStats(); }} />}

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Client?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete <strong>{deleteTarget?.name}</strong> and all their trips and reminders.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
